import type { PaperProps } from '@mantine/core'
import { Paper, Stack } from '@mantine/core'
import { useGlobalState } from '@opsopti/shared'
import type { IconProps } from '@tabler/icons-react'
import { IconFolder, IconMapPin, IconPackage, IconScan, IconUsers, IconViewfinder } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import type { ComponentType } from 'react'
import { useMemo } from 'react'

import { AssetAssignmentsTable } from '@/components/AssetAssignmentsTable'
import { AssetGroupsTable } from '@/components/AssetGroupsTable'
import { AssetsTable } from '@/components/AssetsTable'
import { AssetTargetsTable } from '@/components/AssetTargetsTable'
import { AssetTrackersTable } from '@/components/AssetTrackersTable'
import type { ScreenProps } from '@/components/Screen'
import { Screen } from '@/components/Screen'
import { UsersTable } from '@/components/UsersTable'

type TableScreenProps = {
  entity: 'assets' | 'groups' | 'trackers' | 'targets' | 'users' | 'assignments'
  grow?: boolean
}

export const TableScreen = observer(function TableScreen(props: TableScreenProps) {
  const { uiStore } = useGlobalState()

  const Table = useMemo(() => {
    switch (props.entity) {
      case 'assets':
        return <AssetsTable />
      case 'groups':
        return <AssetGroupsTable />
      case 'trackers':
        return <AssetTrackersTable />
      case 'targets':
        return <AssetTargetsTable />
      case 'users':
        return <UsersTable />
      case 'assignments':
        return <AssetAssignmentsTable />
    }

    return null
  }, [props.entity])

  const screenTitle: Record<TableScreenProps['entity'], string> = {
    assets: 'Assets',
    groups: 'Asset Groups',
    trackers: 'Asset Trackers',
    targets: 'Assignment Targets',
    users: 'Users',
    assignments: 'Activity',
  }

  const ScreenProps: Partial<Record<TableScreenProps['entity'], Omit<ScreenProps, 'title' | 'icon'>>> = {
    assignments: { maw: '100%' },
    users: { maw: '100%' },
    assets: { maw: '100%' },
    groups: { maw: '100%' },
    trackers: { maw: '100%' },
    targets: { maw: '100%' },
  }

  const VirtualizedScreenProps: Omit<ScreenProps, 'title' | 'icon'> = props.grow
    ? { h: '100dvh', BodyStackProps: { style: { flexGrow: 1, minHeight: 0 } } }
    : {}

  const PaperProps: PaperProps = props.grow ? { style: { flexGrow: 1, minHeight: 0 } } : {}

  return (
    <Screen
      headerHidden={uiStore.tableHeightState[props.entity] === 'full'}
      icon={tableScreenIcon[props.entity]}
      title={screenTitle[props.entity]}
      description={<TableScreenDescription entity={props.entity} />}
      {...VirtualizedScreenProps}
      {...ScreenProps[props.entity]}>
      <Paper component={Stack} radius="md" p="lg" shadow="sm" {...PaperProps}>
        {Table}
      </Paper>
    </Screen>
  )
})

export function TableScreenDescription(props: Pick<TableScreenProps, 'entity'>) {
  if (props.entity === 'assets') {
    return 'An asset is a physical item that can be tracked in the system. Assets can have multiple trackers.'
  }

  if (props.entity === 'groups') {
    return `A asset group is used to categorize / organize assets by type.`
  }

  if (props.entity === 'trackers') {
    return "Trackers are methods by which an asset can be assigned to targets. This can be NFC tags, QR codes, or any other method by which an asset's movements are tracked in the system."
  }

  if (props.entity === 'targets') {
    return `An assignment target is where your assets are being assigned to. Targets represent either a physical location (e.g. warehouse storage) where an asset can be located, or a virtual concept like a team or its status (e.g. "in maintenance"). For example, when a user is starting a shift, they can "check out" (i.e. assign) an asset to a team (target). Similarly, when they end a shift, they can "check in" the asset to a storage room (target).`
  }

  if (props.entity === 'users') {
    return 'Users are people who can access the system. Users can be assigned different roles and permissions to control what they can do in the system.'
  }

  if (props.entity === 'assignments') {
    return 'The history of assets being assigned to targets (or other assets). The easiest way to create asset assignments is to use the Companion Application, however this can also be done manually here.'
  }

  return null
}

export const tableScreenIcon: Record<TableScreenProps['entity'], ComponentType<IconProps>> = {
  assets: IconPackage,
  groups: IconFolder,
  trackers: IconScan,
  targets: IconViewfinder,
  users: IconUsers,
  assignments: IconMapPin,
}

export const AssetSentryAssetsScreen = () => <TableScreen grow entity="assets" />
export const AssetSentryGroupsScreen = () => <TableScreen grow entity="groups" />
export const AssetSentryTrackersScreen = () => <TableScreen grow entity="trackers" />
export const AssetSentryTargetsScreen = () => <TableScreen grow entity="targets" />
export const AssetSentryAssignmentsScreen = () => <TableScreen grow entity="assignments" />
export const UsersScreen = () => <TableScreen grow entity="users" />
